import { RECOMMENDED_GREETING_CARD_DESIGN_FILE_SIZE_MB } from '@giftie/shopify-server/utils/constants'

export type UploadedImageDetails = {
  width: number
  height: number
  byteSize: number
  mimeType: string
  fileName: string
}

export type UploadCompletePayload = {
  fileUrl?: string
  printAsset?: unknown
  imageWidth?: number
  imageHeight?: number
  byteSize?: number
  previewByteSize?: number
}

export type ImageUploaderFileType
  = | 'logo_on_white'
    | 'logo_on_coloured'
    | 'greeting_card_design'
    | 'add_on_product_image'

export type PreviewUpload = {
  fileData: string
  mimeType: string
  fileName: string
  byteSize: number
}

export async function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader()
    fileReader.addEventListener('load', (event) => {
      resolve((event.target?.result as string) || '')
    })
    fileReader.addEventListener('error', () =>
      reject(new Error('Failed to read file')),
    )
    fileReader.readAsDataURL(file)
  })
}

async function loadImageElement(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', () =>
      reject(new Error('Failed to read image dimensions')),
    )
    image.src = src
  })
}

export async function getImageDetails(
  file: File,
): Promise<UploadedImageDetails> {
  const objectUrl = URL.createObjectURL(file)

  try {
    const image = await loadImageElement(objectUrl)
    return {
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
      byteSize: file.size,
      mimeType: file.type,
      fileName: file.name,
    }
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

async function blobToDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader()
    fileReader.addEventListener('load', (event) => {
      resolve((event.target?.result as string) || '')
    })
    fileReader.addEventListener('error', () =>
      reject(new Error('Failed to encode preview image')),
    )
    fileReader.readAsDataURL(blob)
  })
}

async function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number,
) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create image preview'))
          return
        }
        resolve(blob)
      },
      type,
      quality,
    )
  })
}

export async function buildGreetingCardPreview(
  file: File,
): Promise<PreviewUpload | null> {
  if (file.type === 'image/svg+xml') return null

  const objectUrl = URL.createObjectURL(file)

  try {
    const image = await loadImageElement(objectUrl)
    const imageWidth = image.naturalWidth || image.width
    const imageHeight = image.naturalHeight || image.height
    const longestEdge = Math.max(imageWidth, imageHeight)
    const scale = longestEdge > 1400 ? 1400 / longestEdge : 1
    const width = Math.max(1, Math.round(imageWidth * scale))
    const height = Math.max(1, Math.round(imageHeight * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')
    if (!context) throw new Error('Failed to create image preview')

    context.drawImage(image, 0, 0, width, height)

    let previewBlob: Blob
    let previewMimeType = 'image/webp'

    try {
      previewBlob = await canvasToBlob(canvas, previewMimeType, 0.88)
    } catch {
      previewMimeType = 'image/png'
      previewBlob = await canvasToBlob(canvas, previewMimeType)
    }

    return {
      fileData: await blobToDataUrl(previewBlob),
      mimeType: previewMimeType,
      fileName: file.name.replace(
        /\.[^.]+$/,
        previewMimeType === 'image/webp' ? '.webp' : '.png',
      ),
      byteSize: previewBlob.size,
    }
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export function validateImageUpload(input: {
  file: File
  fileType: ImageUploaderFileType
  maxSizeBytes: number
  maxSizeMB: number
  maxSizeErrorMessage?: string
}) {
  if (input.file.size > input.maxSizeBytes) {
    if (input.maxSizeErrorMessage) return input.maxSizeErrorMessage

    if (input.fileType === 'greeting_card_design') {
      return `File size must be less than ${input.maxSizeMB}MB. Keeping design previews under ${RECOMMENDED_GREETING_CARD_DESIGN_FILE_SIZE_MB}MB is recommended for faster storefront loading and print processing.`
    }

    return `File size must be less than ${input.maxSizeMB}MB`
  }

  if (!input.file.type.startsWith('image/')) {
    return 'Only image files are allowed'
  }

  return null
}
